import { ISphericalCoords, IScreenCoords, IViewportBounds } from '@newroom-connect/library/interfaces';

/**
 * Static helper utilities for coordinate validation.
 * Contains pure functions for validating different coordinate types.
 */
export class CoordinateValidationHelper {
  /**
   * Validate screen coordinates.
   * @param coords - The coordinates to validate.
   * @returns True if coordinates are valid screen coordinates.
   */
  public static isValidScreenCoords(coords: unknown): coords is IScreenCoords {
    if (!coords || typeof coords !== 'object' || coords === null) {
      return false;
    }
    const copyCoords = coords as Record<string, unknown>;

    return typeof copyCoords['x'] === 'number' && typeof copyCoords['y'] === 'number' && !isNaN(copyCoords['x']) && !isNaN(copyCoords['y']) && isFinite(copyCoords['x']) && isFinite(copyCoords['y']);
  }

  /**
   * Validate spherical coordinates.
   * @param coords - The coordinates to validate.
   * @returns True if coordinates are valid spherical coordinates.
   */
  public static isValidSphericalCoords(coords: unknown): coords is ISphericalCoords {
    if (!coords || typeof coords !== 'object' || coords === null) {
      return false;
    }
    const copyCoords  = coords as Record<string, unknown>;

    return typeof copyCoords['yaw'] === 'number' && typeof copyCoords['pitch'] === 'number' && !isNaN(copyCoords['yaw']) && !isNaN(copyCoords['pitch']) && isFinite(copyCoords['yaw']) && isFinite(copyCoords['pitch']);
  }

  /**
   * Clamp screen coordinates to viewport bounds.
   * @param coords - The screen coordinates to clamp.
   * @param bounds - The viewport bounds to clamp within.
   * @returns Clamped screen coordinates.
   */
  public static clampToViewport(coords: IScreenCoords, bounds: IViewportBounds): IScreenCoords {
    return { x: Math.max(0, Math.min(coords.x, bounds.width)), y: Math.max(0, Math.min(coords.y, bounds.height)) };
  }

  /**
   * Check if screen coordinates are within viewport bounds.
   * @param coords - The screen coordinates to check.
   * @param bounds - The viewport bounds to check against.
   * @returns True if coordinates are within viewport bounds.
   */
  public static isWithinViewport(coords: IScreenCoords, bounds: IViewportBounds): boolean {
    return coords.x >= 0 && coords.x <= bounds.width && coords.y >= 0 && coords.y <= bounds.height;
  }

  /**
   * Validate array of corner coordinates.
   *
   * @param corners - Array of corner coordinates to validate.
   *
   * @returns True if all corners are valid screen coordinates.
   */
  public static isValidCornerArray(corners: IScreenCoords[]): boolean {
    if (corners.length !== 4) {
      return false;
    }

    return corners.every(corner => this.isValidScreenCoords(corner));
  }

  /**
   * Calculate quadrilateral area using shoelace formula.
   *
   * @param corners - Array of corner coordinates in order (topLeft, topRight, bottomLeft, bottomRight).
   *
   * @returns
   */
  public static calculateQuadrilateralArea(corners: IScreenCoords[]): number {
    if (corners.length !== 4) {
      return 0;
    }

    // Reorder corners for shoelace formula: topLeft, topRight, bottomRight, bottomLeft
    const orderedCorners = [corners[0], corners[1], corners[3], corners[2]];

    let area = 0;
    const n = orderedCorners.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;

      area += orderedCorners[i].x * orderedCorners[j].y;
      area -= orderedCorners[j].x * orderedCorners[i].y;
    }

    return Math.abs(area) / 2;
  }

  /**
   * Validate transform by checking area and coordinate validity.
   *
   * @param corners - Array of corner coordinates to validate.
   * @param minArea - Minimum area threshold to prevent degenerate transforms.
   *
   * @returns True if transform is valid.
   */
  public static isValidTransform(corners: IScreenCoords[], minArea = 100): boolean {
    if (!this.isValidCornerArray(corners)) {
      return false;
    }

    const area = this.calculateQuadrilateralArea(corners);

    return area >= minArea;
  }
}
